/**
 * 兵庫県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「神戸牛」ではなく「神戸ビーフ（神戸肉）」。GI登録名称・協議会の定義名称に合わせる。
 *  - 神戸ビーフは品種ではなく但馬牛のこと。農水省が明記している。
 *  - 「日本三大和牛」は公式資料で裏が取れない。書かない。
 *  - 明石焼の名は1988年ごろのPR名。地元の呼称は「玉子焼」。
 *  - 姫路おでんの名称は2006年に有志が命名したもの。「江戸時代から」と書かない。
 *  - いかなごのくぎ煮を「神戸発祥」と断定しない（農水省も「とされ」）。
 *  - そばめしを「長田発祥」と断定しない。店名も出さない。
 *  - 香住ガニ（ベニズワイガニ）と松葉ガニ（ズワイガニ）を混同しない。
 *  - 淡路島たまねぎはGI登録産品ではない。「生産量日本一」も裏が取れない。
 *  - 揖保乃糸は登録商標。表示名は「播州そうめん」にする。
 *
 * 本番画像は public/assets/foods/hyogo/ に置く。
 *
 * 2026-10-05 かにの重なりを解消（相島さん決定）。
 * - Lv10 香住ガニ（ベニズワイガニ）を外し、淡路島の鱧（はもすき）に差し替えた。Lv10・11 が続けてかにで、盤面で見分けにくかったため。
 *   ベニズワイガニは鳥取・富山にもある
 * - Lv11 の表示名を「松葉がに」から「津居山かに」に変えた（料理ID matsuba-gani はそのまま）。鳥取 Lv12「松葉がに」と同じ名前だったため。
 *   説明はもとから豊岡市・津居山港のかに（足の青いタグ）の話
 * - はもすき：淡路島観光協会のページに「沼島発祥」とあるが、発祥は書かない（CLAUDE.md の方針）
 * - はもすき：特定の旅館・店名は書かない。玉ねぎを入れるなどの具は、開いた出典で確かめていないので書かない
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：丹波黒大豆・いかなごのくぎ煮・淡路島たまねぎ（店で食べられる品に差し替え）
 * - 淡路島牛丼：発売1年の売上・経済効果の数字は、出典はあるが図鑑の説明として不要なので書かない
 * - 淡路島牛丼：「教科書にも掲載」は出典ページにあるが、どの教科書か確かめられないので書かない
 * - 淡路島牛丼：「A級グルメ」「島のソウルフード」は事務局の宣伝文句なので書かない
 * - 淡路島牛丼：特定の加盟店名は書かない
 * - 出石皿そば：「何皿食べられるか」の大食い的な紹介は、出典に「そば喰い大会」の記述はあるが食べ放題のように誤読されるので書かない
 * - 出石皿そば：特定のそば店名は書かない
 * - ぼたん鍋：文化庁「100年フード」認定は検索結果の要約に出たが、開いたページでは確かめていないので書かない
 * - ぼたん鍋：名前の由来（昭和6年の篠山小唄の歌詞）は出典にあるが、説明が長くなるので今回は書いていない（書くなら同じ出典で可）
 * - ぼたん鍋：特定の料理旅館・店名は書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { HYOGO_FOOD_HULLS } from './hyogoFoodShapes.generated';
import { hyogoSceneryTour } from './hyogoScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'himeji-oden',
    level: 1,
    name: '姫路おでん',
    shortName: '姫路おでん',
    image: '/assets/foods/hyogo/01_himeji_oden.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '生姜醤油をかけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/40_6_hyogo.html',
    icon: { label: 'おでん', shape: 'pot', baseColor: '#c9a45a', accentColor: '#e8e0cc' },
    reward: {
      unlockScore: 200,
      area: '姫路',
      headline: '生姜醤油をかける、あるいはつけて食べるのが最大の特徴。濃い甘味と薄味の2種類があるが、生姜醤油ならどちらも姫路おでん。',
      tip: '姫路がかつて生姜と醤油の産地だったことが起こりと考えられている。「姫路おでん」という名前は2006年に地域活性化の有志が付けた。',
      links: searchLinks('姫路市', '姫路おでん'),
    },
  },
  {
    id: 'akashiyaki',
    level: 2,
    name: '明石焼（玉子焼）',
    shortName: '明石焼',
    image: '/assets/foods/hyogo/02_akashiyaki.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'だし汁につけて食べる、卵の生地。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/40_2_hyogo.html',
    icon: { label: '明石焼', shape: 'dumpling', baseColor: '#f0d88c', accentColor: '#c9a45a' },
    reward: {
      unlockScore: 500,
      area: '明石',
      headline: '地元では、もともと「玉子焼」と呼ぶ。「明石焼」の名は1988年ごろ、市の職員がPRのために付けたもの。',
      tip: '小麦粉・じん粉・卵・だし汁・タコで作り、だし汁につけて食べる。人工サンゴ「明石玉」を作るときに余った卵黄と、地元のタコを合わせたのが起こりとされる。',
      links: searchLinks('明石市', '明石焼 玉子焼'),
    },
  },
  {
    id: 'akashi-dako',
    level: 3,
    name: '明石だこ',
    shortName: '明石だこ',
    image: '/assets/foods/hyogo/03_akashi_dako.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '流れの速い海峡で、足が太く短くなる。',
    source: 'https://www.city.akashi.lg.jp/seisaku/seisaku_shitsu/20130701_takaramono_3.html',
    icon: { label: 'たこ', shape: 'fish', baseColor: '#c46a6a', accentColor: '#f0e0d0' },
    reward: {
      unlockScore: 1000,
      area: '明石',
      headline: '流れの速い明石海峡で育つので足が太く短く、陸に上げても立って歩くほど力が強い。旬は6月末からお盆ごろ。',
      tip: '漁法は底曳き網漁、タコつぼ漁、一本釣り。農林水産省は、兵庫県のタコ捕獲量は全国2位、マダコでは日本一と記している。',
      links: searchLinks('明石市', '明石だこ'),
    },
  },
  {
    id: 'banshu-somen',
    level: 4,
    name: '播州そうめん',
    shortName: '播州そうめん',
    image: '/assets/foods/hyogo/04_banshu_somen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '600年の手延べと、倉での熟成。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0154/index.html',
    icon: { label: 'そうめん', shape: 'noodle-bowl', baseColor: '#f4efe4', accentColor: '#8fa9c4' },
    reward: {
      unlockScore: 1800,
      area: '播磨・たつの',
      headline: '2024年8月27日に地理的表示（GI）第154号へ登録。茹で伸びしにくく、なめらかでコシがある。',
      tip: '斑鳩寺の古文書には応永25年（1418年）の記載がある。播州平野の小麦、揖保川の清流、赤穂の塩がそろったことが産地になった理由。「厄」と呼ばれる倉庫で寝かせる。',
      links: searchLinks('たつの市', '播州そうめん'),
    },
  },
  {
    id: 'sobameshi',
    level: 5,
    name: 'そばめし',
    shortName: 'そばめし',
    image: '/assets/foods/hyogo/05_sobameshi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '焼きそばとご飯を、鉄板で刻んで混ぜる。',
    source: 'https://www.feel-kobe.jp/column/nagata-gourmet/',
    icon: { label: 'そばめし', shape: 'curry-plate', baseColor: '#8a6a44', accentColor: '#d8c49c' },
    reward: {
      unlockScore: 3000,
      area: '神戸・長田',
      headline: '神戸市の公式観光サイトは「長田発祥とされ」と紹介している。ソース焼きそばとご飯を細かく刻んで混ぜ合わせる。',
      tip: '近くの工場で働く客が、持ってきた冷や飯を焼きそばと一緒に温めてほしいと頼んだことから生まれたとされる。同じ長田には、牛スジとコンニャクを甘辛く煮た「すじこん」をのせる「すじ焼き」もある。',
      links: searchLinks('神戸市長田区', 'そばめし'),
    },
  },
  {
    id: 'katsumeshi',
    level: 6,
    name: 'かつめし',
    shortName: 'かつめし',
    image: '/assets/foods/hyogo/06_katsumeshi.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '箸で食べる洋食。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/40_4_hyogo.html',
    icon: { label: 'かつめし', shape: 'curry-plate', baseColor: '#5c3a26', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 4500,
      area: '加古川',
      headline: '洋食の皿に盛ったご飯の上に平たいビーフカツをのせ、デミグラスソースをかけて、ゆでキャベツを添える。',
      tip: '戦後まもなく、ビーフカツがまだ珍しかったころ、街の食堂が「ナイフとフォークがなくても箸で気軽に食べられる洋食」として考え出した。',
      links: searchLinks('加古川市', 'かつめし'),
    },
  },
  {
    id: 'awajishima-gyudon',
    level: 7,
    name: '淡路島牛丼',
    shortName: '淡路島牛丼',
    image: '/assets/foods/hyogo/07_awajishima_gyudon.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '牛肉・玉ねぎ・米をすべて淡路島産でそろえる牛丼。2008年に生まれ、決まりを守る島内の加盟店が出している。産地のほかは各店の自由な工夫を認めていて、店ごとに味が違う。',
    source:
      'https://www.awa-gyu.net/',
    icon: { label: '牛丼', shape: 'rice-bowl', baseColor: '#b8763a', accentColor: '#e8c46a' },
    reward: {
      unlockScore: 6500,
      area: '淡路島（洲本市・淡路市・南あわじ市）',
      headline: '牛肉も玉ねぎも米も島のもの',
      tip: '淡路島の玉ねぎは10月の種まきから6月の収穫まで約8か月かけて育ち、玉ねぎ小屋で自然乾燥させる。辛みが少なく糖度が高い。',
      links: searchLinks('淡路島', '淡路島牛丼'),
      official: [{ label: '淡路島牛丼（淡路島観光協会）', url: 'https://www.awa-gyu.net/' }],
    },
  },
  {
    id: 'izushi-sara-soba',
    level: 8,
    name: '出石皿そば',
    shortName: '出石皿そば',
    image: '/assets/foods/hyogo/08_izushi_sara_soba.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: 'そば粉につなぎの小麦粉を加えて打ったそばを、直径約13cmの出石焼の白い小皿に少しずつ盛る。5枚1組がひとり前。徳利のつゆをそば猪口に注ぎ、卵・とろろ・ねぎなどの薬味を入れてつけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/40_10_hyogo.html',
    icon: { label: '皿そば', shape: 'noodle-bowl', baseColor: '#f4f1ea', accentColor: '#8a7a62' },
    reward: {
      unlockScore: 9000,
      area: '豊岡市出石町',
      headline: '白い小皿に少しずつ盛るそば',
      tip: '1706年に信州上田から国替えで来た藩主・仙石政明が連れてきたそば職人に始まるとされる。小皿は、幕末に屋台で持ち運びやすいよう浅い手塩皿に盛ったのが始まり。',
      links: searchLinks('豊岡市出石町', '出石皿そば'),
      official: [{ label: '豊岡市「出石皿そば」', url: 'https://www.city.toyooka.lg.jp/kanko/taberu/1002200.html' }],
    },
  },
  {
    id: 'botan-nabe',
    level: 9,
    name: 'ぼたん鍋',
    shortName: 'ぼたん鍋',
    image: '/assets/foods/hyogo/09_botan_nabe.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '昆布とかつおのだしに白みそと赤みそを合わせた汁で、猪肉とハクサイ・ゴボウ・ネギ・焼き豆腐などを煮る鍋。猪肉は牡丹の花びらのように並べて出す。丹波篠山で冬や正月、来客のときに出すもてなしの料理。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/40_3_hyogo.html',
    icon: { label: 'ぼたん鍋', shape: 'hotpot', baseColor: '#9a4a2e', accentColor: '#f2e6d8' },
    reward: {
      unlockScore: 12000,
      area: '丹波篠山市',
      headline: '猪肉を牡丹の花に盛る',
      tip: '明治41年ごろ篠山に駐屯した陸軍の連隊が、訓練で捕らえた猪の肉をみそ汁に入れて食べたのが始まりといわれる。はじめは「いの鍋」と呼ばれ、好みで粉山椒をふって食べる。',
      links: searchLinks('丹波篠山市', 'ぼたん鍋'),
    },
  },
  {
    id: 'awaji-hamo',
    level: 10,
    name: '淡路島の鱧（はもすき）',
    shortName: 'はもすき',
    image: '/assets/foods/hyogo/10_awaji_hamo.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '鳴門海峡の潮で身が引き締まった、夏の鱧の鍋。',
    source: 'https://www.city.minamiawaji.hyogo.jp/soshiki/shoku/hamo.html',
    icon: { label: 'はもすき', shape: 'hotpot', baseColor: '#d9a441', accentColor: '#fbf3e4' },
    reward: {
      unlockScore: 16000,
      area: '南あわじ市',
      headline: '旬は例年6月ごろから9月ごろ。「鱧は梅雨の雨を飲んで旨くなる」という言葉がある。',
      tip: '沼島近海の柔らかい砂地で育ち、延縄で1本ずつ釣り上げる。湯引きには800グラム前後の小ぶりなもの、はもすきには脂ののった1.5キロ前後を使う。傷が少なく金色に光る魚体から「べっぴん鱧」「黄金鱧」とも呼ばれる。',
      links: searchLinks('南あわじ市', 'はもすき'),
    },
  },
  {
    id: 'matsuba-gani',
    level: 11,
    name: '津居山かに',
    shortName: '津居山かに',
    image: '/assets/foods/hyogo/11_matsuba_gani.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '足の青いタグが、津居山の印。',
    source: 'https://www.city.toyooka.lg.jp/kanko/taberu/1002193.html',
    icon: { label: '津居山かに', shape: 'feast', baseColor: '#b5563f', accentColor: '#f4d9a8' },
    reward: {
      unlockScore: 22000,
      area: '但馬',
      headline: '山陰ではズワイガニのオスを「松葉ガニ」、メスを「セコガニ」と呼ぶ。漁期は11月6日解禁から翌年3月20日まで。',
      tip: '津居山かには丹後半島沖が漁場で日帰りの操業ができるため、味も鮮度もよい。足に結んだ青いタグが目印。県北西端の浜坂漁港をはじめ5つの港に水揚げされる。',
      links: searchLinks('豊岡市', '津居山かに'),
    },
  },
  {
    id: 'kobe-beef',
    level: 12,
    name: '神戸ビーフのステーキ',
    shortName: '神戸ビーフ',
    image: '/assets/foods/hyogo/12_kobe_beef.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '但馬牛のなかの、さらにひとにぎり。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0003/index.html',
    icon: { label: '神戸ビーフ', shape: 'curry-plate', baseColor: '#c14a4a', accentColor: '#f4e6d2' },
    reward: {
      unlockScore: 30000,
      area: '兵庫県',
      headline: '地理的表示（GI）第3号、平成27年12月22日の登録。農林水産省は「神戸ビーフという牛の種類は存在しない。神戸ビーフというのは『但馬牛』のことをさしている」と明記している。',
      tip: '協議会の定義では、但馬牛のうち未経産牛・去勢牛で、歩留・肉質等級A／B4等級以上、脂肪交雑BMS値No.6以上のものだけ。素牛の但馬牛は兵庫県北部で改良が重ねられ、県有種雄牛のみを代々交配してきた。',
      links: searchLinks('神戸市', '神戸ビーフ ステーキ'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const hyogoFoodStage: StageDefinition = {
  regionId: 'hyogo',
  regionName: '兵庫県',
  mode: 'food',
  title: '兵庫めしマージ',
  theme: {
    primaryColor: '#5a4f7a',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#17151d',
  },
  /** 背景ツアー「兵庫めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: hyogoSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, HYOGO_FOOD_HULLS),
};
